import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import FractalBackground from './components/FractalBackground';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './components/ThemeProvider';
import AnnouncementBar from './components/AnnouncementBar';
import LoadingScreen from './components/LoadingScreen';
import InteractiveParticles from './components/InteractiveParticles';
import AmbientMusicPlayer from './components/AmbientMusicPlayer';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import BrowseArtists from './pages/BrowseArtists';
import ArtistDetail from './pages/ArtistDetail';
import Dashboard from './pages/Dashboard';
import CreateArtistProfile from './pages/CreateArtistProfile';
import AdminPanel from './pages/AdminPanel';
import AdminAnalytics from './pages/AdminAnalytics';
import FeaturedManagement from './pages/FeaturedManagement';
import ManageAllArtists from './pages/ManageAllArtists';
import BookingConfirmation from './pages/BookingConfirmation';
import ArtistDashboard from './pages/ArtistDashboard';
import ArtistCalendar from './pages/ArtistCalendar';
import ProfileSettings from './pages/ProfileSettings';
import Favorites from './pages/Favorites';
import Referrals from './pages/Referrals';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import Help from './pages/Help';
import ProtectedRoute from './components/ProtectedRoute';
import NotFound from './pages/NotFound';
import { Toaster } from './components/ui/sonner';
// New Feature Pages
import NotificationCenter from './pages/NotificationCenter';
import SupportTickets from './pages/SupportTickets';
import SubscriptionPlans from './pages/SubscriptionPlans';
import LoyaltyProgram from './pages/LoyaltyProgram';
import Blog from './pages/Blog';
import Live from './pages/Live';
import LiveRoom from './pages/LiveRoom';
import BlogPost from './pages/BlogPost';
import Events from './pages/Events';
import Disputes from './pages/Disputes';
import AdminContentModeration from './pages/AdminContentModeration';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminDisputes from './pages/AdminDisputes';
import AdminManagement from './pages/AdminManagement';
import AdminCancellations from './pages/AdminCancellations';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AdminAdmins from './pages/AdminAdmins';
import AdminSiteContent from './pages/AdminSiteContent';
import AdminTicketScanner from './pages/AdminTicketScanner';
import AdminEventStats from './pages/AdminEventStats';
import ApiDocs from './pages/ApiDocs';
import SecuritySettings from './pages/SecuritySettings';
// Square Payment & Payout Pages (removed)
import ArtistBookings from './pages/ArtistBookings';
import CommunityChat from './pages/CommunityChat';
// Settings Pages
import ArtistSettings from './pages/ArtistSettings';
// New Feature Pages
import ArtistGallery from './pages/ArtistGallery';
import ArtistRider from './pages/ArtistRider';
import ArtistAnalytics from './pages/ArtistAnalytics';
import ArtistSearch from './pages/ArtistSearch';
import ContractManagement from './pages/ContractManagement';
import ChatConversation from './pages/ChatConversation';
import GroupChats from './pages/GroupChats';
import ReferralProgram from './pages/ReferralProgram';
import ClientAnalytics from './pages/ClientAnalytics';
import Recommendations from './pages/Recommendations';
// Nightclub Game
import Nightclub from './pages/Nightclub';

// Component to conditionally render AmbientMusicPlayer based on route
function ConditionalMusicPlayer({ autoPlay }) {
  const location = useLocation();
  // Don't show music player on nightclub page (it has its own music via YouTube)
  if (location.pathname === '/nightclub') {
    return null;
  }
  return <AmbientMusicPlayer autoPlay={autoPlay} />;
}

function AppRouter({ musicAutoPlay }) {
  const location = useLocation();

  // Handle auth callback synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      {/* ConditionalMusicPlayer temporarily disabled for debugging */}
      <Navigation />
      <AnnouncementBar />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route path="/browse" element={<BrowseArtists />} />
      <Route path="/artist/:artistId" element={<ArtistDetail />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={<ArtistSearch />}
      />
      <Route
        path="/artist/create"
        element={
          <ProtectedRoute>
            <CreateArtistProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist-dashboard"
        element={
          <ProtectedRoute>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/featured"
        element={
          <ProtectedRoute>
            <FeaturedManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/artists"
        element={
          <ProtectedRoute>
            <ManageAllArtists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/calendar"
        element={
          <ProtectedRoute>
            <ArtistCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking-confirmation"
        element={
          <ProtectedRoute>
            <BookingConfirmation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/settings"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <Favorites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <Referrals />
          </ProtectedRoute>
        }
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/help" element={<Help />} />
      {/* New Feature Routes */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportTickets />
          </ProtectedRoute>
        }
      />
      <Route path="/subscriptions" element={<SubscriptionPlans />} />
      <Route
        path="/loyalty"
        element={
          <ProtectedRoute>
            <LoyaltyProgram />
          </ProtectedRoute>
        }
      />
      <Route path="/blog" element={<Blog />} />
      <Route path="/live" element={<Live />} />
      <Route path="/live/:roomId" element={<LiveRoom />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/events" element={<Events />} />
      <Route path="/community-chat" element={<CommunityChat />} />
      <Route
        path="/nightclub"
        element={<Nightclub />}
      />
      <Route
        path="/disputes"
        element={
          <ProtectedRoute>
            <Disputes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/moderation"
        element={
          <ProtectedRoute>
            <AdminContentModeration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promo-codes"
        element={
          <ProtectedRoute>
            <AdminPromoCodes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/disputes"
        element={
          <ProtectedRoute>
            <AdminDisputes />
          </ProtectedRoute>
        }
      />
      <Route path="/api-docs" element={<ApiDocs />} />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecuritySettings />
          </ProtectedRoute>
        }
      />
      {/* Artist Booking & Payout Routes */}
      <Route
        path="/artist/bookings"
        element={
          <ProtectedRoute>
            <ArtistBookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/settings"
        element={
          <ProtectedRoute>
            <ArtistSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/gallery"
        element={
          <ProtectedRoute>
            <ArtistGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/rider"
        element={
          <ProtectedRoute>
            <ArtistRider />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/analytics"
        element={
          <ProtectedRoute>
            <ArtistAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/:bookingId/contract"
        element={
          <ProtectedRoute>
            <ContractManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:bookingId"
        element={
          <ProtectedRoute>
            <ChatConversation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/group-chats"
        element={
          <ProtectedRoute>
            <GroupChats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/group-chats/:groupId"
        element={
          <ProtectedRoute>
            <GroupChats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralProgram />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-analytics"
        element={
          <ProtectedRoute>
            <ClientAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/for-you"
        element={
          <ProtectedRoute>
            <Recommendations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage"
        element={
          <ProtectedRoute>
            <AdminManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cancellations"
        element={
          <ProtectedRoute>
            <AdminCancellations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute>
            <AdminAnnouncements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/admins"
        element={
          <ProtectedRoute>
            <AdminAdmins />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/site-content"
        element={
          <ProtectedRoute>
            <AdminSiteContent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tickets/scan"
        element={
          <ProtectedRoute>
            <AdminTicketScanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events/stats"
        element={
          <ProtectedRoute>
            <AdminEventStats />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <Footer />
    </>
  );
}

function App() {
  const [showLoading, setShowLoading] = useState(() => {
    // Skip intro overlay for automated tests (Playwright) and only show on first visit.
    const isAutomation = typeof navigator !== 'undefined' && !!navigator.webdriver;
    if (isAutomation) return false;

    const hasVisited = sessionStorage.getItem('hasVisitedRazient');
    return !hasVisited;
  });
  const [musicAutoPlay, setMusicAutoPlay] = useState(false);

  const handleLoadingComplete = () => {
    sessionStorage.setItem('hasVisitedRazient', 'true');
    setShowLoading(false);
    // Trigger music auto-play after entering
    setMusicAutoPlay(true);
  };

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <div className="App">
          {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
          <InteractiveParticles />
          <BrowserRouter>
            <AppRouter musicAutoPlay={musicAutoPlay} />
          </BrowserRouter>
          <Toaster />
        </div>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
